/**
 * rentalActions.ts — Single Source of Truth สำหรับ "action ที่ทำได้" ของรายการเช่า
 *
 * ใช้ร่วมกันทั้งหน้า List (RentalPage) และหน้า Detail (RentalViewPage) เพื่อให้ปุ่ม/เมนู
 * ที่ผู้ใช้เห็นสอดคล้องกันเสมอ และตรงกับ state machine ของ backend
 * (ดู VALID_RENTAL_TRANSITIONS ใน backend/app/api/v1/endpoints/rentals.py)
 *
 * หลักการ:
 *   1. "ไม่แสดง = ทำไม่ได้" — ปุ่มที่โผล่ต้องกดแล้วสำเร็จเสมอ (ไม่มี 400 จาก state ผิด)
 *   2. แก้ gating ที่ไฟล์เดียว → ทั้งสองหน้าได้ผลพร้อมกัน
 *
 * Backend state machine:
 *   DRAFT                → ACTIVE, CANCELLED
 *   ACTIVE               → PENDING_CANCELLATION, COMPLETED, CANCELLED
 *   PENDING_CANCELLATION → ACTIVE, CANCELLED
 *   COMPLETED / CANCELLED = terminal
 *
 * Backend guard ที่สำคัญ:
 *   - แก้ไข (PUT /rentals/{id}) : เฉพาะ DRAFT, ACTIVE  (อื่น ๆ ถูก block ที่ API)
 *   - อนุมัติ (/approve)        : เฉพาะ DRAFT  (perm rentals.approve)
 *   - รับคืน (/return)          : เฉพาะ ACTIVE → COMPLETED
 *   - ยกเลิก (/cancel)          : DRAFT, ACTIVE, PENDING_CANCELLATION
 *   - ลบ (DELETE soft)          : ฝั่ง UI จำกัด COMPLETED, CANCELLED
 */

import { RentalStatus } from '../services/rentalService';

export interface RentalActionContext {
  hasPermission: (perm: string) => boolean;
  canView: (module: string) => boolean;
  canEdit: (module: string) => boolean;
  canDelete: (module: string) => boolean;
}

/** โหมดของปุ่ม "ยกเลิก" ต่างกันตามสถานะ */
export type CancelMode =
  | 'void'      // DRAFT: ยังไม่ได้ส่งมอบอุปกรณ์ → ยกเลิกร่างทันที (ไม่ต้องเปิด flow ติดตามคืน)
  | 'flow'      // ACTIVE: อุปกรณ์ถูกใช้งานอยู่ → เปิด CancellationFlowModal (สร้างใบแจ้งยกเลิก + ติดตามคืน)
  | 'continue'  // PENDING_CANCELLATION: อยู่ในกระบวนการแล้ว → ไปหน้าติดตาม
  | null;

export interface RentalActionInput {
  status?: RentalStatus | string | null;
  actual_return_date?: string | null;
  contract_id?: number | null;
  extension_count?: number | null;
  // S122-EXTQUEUE (PO 2026-08-19): จำนวน "คำขอ" ต่ออายุทุกสถานะ (pending/approved/rejected)
  // จาก list API — ต่างจาก extension_count ที่นับเฉพาะใบอนุมัติแล้ว
  extension_request_count?: number | null;
}

export interface RentalActionAvailability {
  // กลุ่ม "ดู"
  view: boolean;
  viewContract: boolean;
  viewNotes: boolean;
  viewHistory: boolean;
  viewExtensionHistory: boolean;
  // กลุ่ม "จัดการ"
  approve: boolean;        // DRAFT → เริ่มเช่า
  edit: boolean;           // DRAFT / ACTIVE
  extend: boolean;         // ACTIVE
  receiveReturn: boolean;  // ACTIVE → ปิดงานทันที (/return → COMPLETED)
  requestReturn: boolean;  // เปิดการติดตามรับคืน (equipment-returns)
  // กลุ่ม "ยุติ"
  cancel: boolean;
  cancelMode: CancelMode;
  // กลุ่ม "ลบ"
  delete: boolean;
  // กลุ่ม "มัดจำ" (Deposit v2): บันทึกรับ/คืน/คง/ปลด/แก้ไขบันทึกคืน — เช็คสถานะต่อ item ที่หน้าใช้งาน
  manageDeposit: boolean;
}

const ACTIVE = RentalStatus.ACTIVE;
const DRAFT = RentalStatus.DRAFT;
const PENDING = RentalStatus.PENDING_CANCELLATION;
const COMPLETED = RentalStatus.COMPLETED;
const CANCELLED = RentalStatus.CANCELLED;

/**
 * คืน availability ของทุก action สำหรับ rental หนึ่งรายการ ตามสถานะ + สิทธิ์ผู้ใช้
 */
export function getRentalActions(
  rental: RentalActionInput | null | undefined,
  ctx: RentalActionContext,
): RentalActionAvailability {
  const s = (rental?.status ?? '') as RentalStatus | string;
  const returned = !!rental?.actual_return_date;
  const { hasPermission, canView, canEdit, canDelete } = ctx;

  const isDraft = s === DRAFT;
  const isActive = s === ACTIVE;
  const isPending = s === PENDING;
  const isCompleted = s === COMPLETED;
  const isCancelled = s === CANCELLED;

  // ---- กลุ่มยกเลิก: เลือกโหมดตามสถานะ ----
  let cancel = false;
  let cancelMode: CancelMode = null;
  // BUG-QA-026 FIX (S86): cancel gate ต้องมี edit-fallback ให้ตรง backend /cancel
  // (require_permission "rentals.cancel" OR "rentals.edit") + sibling receiveReturn/complete
  // ที่ใช้ edit-fallback อยู่แล้ว. เดิม gate `rentals.cancel` อย่างเดียว → role ที่มี edit แต่ไม่มี
  // cancel (Admin/Viewer/Inventory/Accounting) cancel ผ่าน UI ไม่ได้ทั้งที่ backend อนุญาต.
  if (hasPermission('rentals.cancel') || hasPermission('rentals.edit')) {
    if (isDraft) {
      cancel = true;
      cancelMode = 'void';
    } else if (isActive) {
      cancel = true;
      cancelMode = 'flow';
    } else if (isPending) {
      cancel = true;
      cancelMode = 'continue';
    }
  }

  return {
    // ดู — แสดงตลอดถ้ามีสิทธิ์ view
    view: canView('rentals'),
    viewContract: canView('rentals') && !!rental?.contract_id && hasPermission('contracts.view'),
    viewNotes: canView('rentals'),
    viewHistory: canView('rentals'),
    // S122-EXTQUEUE FIX (PO 2026-08-19): เดิม gate ที่ extension_count (นับเฉพาะใบอนุมัติ)
    // → rental ที่มีแต่คำขอ "รออนุมัติ/ถูกปฏิเสธ" เปิดประวัติจาก UI ไม่ได้เลย
    // ใช้ extension_request_count (นับทุกคำขอ) ร่วมด้วย — ค่าเก่ายังคงไว้เป็น fallback
    // สำหรับ caller ที่ไม่ได้ส่ง field ใหม่ (เช่น detail page ที่ payload ต่างจาก list)
    viewExtensionHistory:
      canView('rentals') &&
      ((rental?.extension_request_count ?? 0) > 0 || (rental?.extension_count ?? 0) > 0),

    // จัดการ
    approve: isDraft && hasPermission('rentals.approve'),
    edit: (isDraft || isActive) && canEdit('rentals'),
    extend: isActive && canEdit('rentals'),
    receiveReturn:
      isActive && !returned && (hasPermission('rentals.edit') || hasPermission('rentals.return_equipment')),
    // ขอคืน/ติดตาม — ยังเปิดให้กับ ACTIVE/PENDING/CANCELLED ที่ยังไม่ได้คืนของจริง
    // (CANCELLED ที่อุปกรณ์ยังไม่ถูกคืน ต้องตามเก็บได้ — data-safety, อิงพฤติกรรมเดิม BUG-S1530-002)
    // OBS-S87-001 FIX (S87): requestReturn เป็น nav-button ไป equipment-returns hub
    // (route gated equipment_returns.view, App.tsx:206). เพิ่ม && equipment_returns.view
    // เพื่อกัน latent dead-end (custom role ที่มี rentals.edit แต่ไม่มี equipment_returns.view
    // เห็นปุ่ม → navigate → access-denied). ทุก seeded role co-grant อยู่แล้ว → ไม่ regress.
    requestReturn:
      (isActive || isPending || isCancelled) &&
      !returned &&
      (hasPermission('rentals.edit') || hasPermission('rentals.request_return')) &&
      hasPermission('equipment_returns.view'),

    // ยุติ
    cancel,
    cancelMode,

    // ลบ — เฉพาะสถานะปลายทาง
    delete: (isCompleted || isCancelled) && canDelete('rentals'),

    // มัดจำ — permission อย่างเดียว (backend endpoint /deposit/* ไม่เช็คสถานะ rental
    // ใช้ได้กับ CANCELLED/COMPLETED); เงื่อนไขต่อ item ดู canReturnDeposit/canMarkDepositPaid ใน depositService
    manageDeposit: hasPermission('rentals.deposit_manage'),
  };
}

/** ป้ายกำกับมาตรฐาน (ใช้ร่วมให้ข้อความตรงกันทั้งระบบ) */
export const RENTAL_ACTION_LABELS = {
  view: 'ดูรายละเอียด',
  viewContract: 'ดูสัญญา',
  viewNotes: 'ดูหมายเหตุ',
  viewHistory: 'ดูประวัติการเปลี่ยนแปลง',
  viewExtensionHistory: 'ดูประวัติการต่อเช่า',
  approve: 'อนุมัติ → เริ่มเช่า',
  edit: 'แก้ไขรายการเช่า',
  extend: 'ต่ออายุการเช่า',
  receiveReturn: 'ของกลับครบแล้ว–ปิดการเช่า',
  requestReturn: 'เปิดใบตามเก็บอุปกรณ์',
  cancelVoid: 'ยกเลิกร่าง',
  cancelFlow: 'ลูกค้าแจ้งเลิกเช่า',
  cancelContinue: 'ดำเนินการยกเลิก (ต่อ)',
  delete: 'ลบรายการเช่า',
  // Deposit v2 — ปุ่มต่อรายการในหน้า Detail
  depositReturn: 'บันทึกการคืนมัดจำ',
  depositUnhold: 'ปลดการคงมัดจำ',
  depositReopen: 'แก้ไขบันทึกคืน',
  depositMarkPaid: 'บันทึกรับมัดจำ',
} as const;

/** ข้อความอธิบายใต้ปุ่ม (ใช้ในหน้า Detail) */
export const RENTAL_ACTION_HINTS = {
  approve: 'อนุมัติร่าง — เริ่มสัญญาเช่าและจองอุปกรณ์',
  receiveReturn: 'บันทึกรับคืนครบ → สถานะเสร็จสิ้น + หยุดบิล (ทำแล้วแก้ไม่ได้)',
  requestReturn: 'ของยังไม่กลับ — ติดตามการคืน (รายการเช่ายังเดินอยู่)',
  cancelVoid: 'ยกเลิกร่างที่ยังไม่เริ่มใช้งาน',
  cancelFlow: 'เริ่มยกเลิก + ติดตามรับคืนอุปกรณ์',
  cancelContinue: 'ไปหน้าติดตามการยกเลิก',
} as const;
